import { useMutation } from '@tanstack/react-query';
import {
  Action,
  Modal,
  SupportingText,
  TextAreaInput,
  TextInput,
} from 'corso-admin-ui';
import { CrewMerchantUi, FinalizationEmailTypeEnum } from 'corso-types';
import { FormEventHandler, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import api from '~/api';
import { useClaim } from '~/features/claimReview/ClaimReviewProvider';
import { useConfigSettings } from '~/hooks/useConfigSettings';
import { useToast } from '~/hooks/useToast';
import { useStoreId } from '~/providers/StoreUserProvider';

export default function RequestMoreInfo({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const storeId = useStoreId();
  const { id, externalId, shippingAddress, customerEmail } = useClaim();

  const { show: showToast } = useToast();

  const { data: emailSettings } = useConfigSettings(({ email }) => email);
  const emailTemplate = useMemo(
    () =>
      emailSettings?.storeEmailTemplates.finalizeEmailTemplates.find(
        (template) =>
          template.type === FinalizationEmailTypeEnum.claimNeedsMoreInfo,
      ),
    [emailSettings?.storeEmailTemplates.finalizeEmailTemplates],
  );

  const { register, handleSubmit, reset } = useForm<
    CrewMerchantUi.ClaimNeedsMoreInfo['notifyCustomerByEmail']
  >({
    defaultValues: {
      emailType: FinalizationEmailTypeEnum.claimNeedsMoreInfo,
      subject: emailTemplate?.subject ?? '',
      header: emailTemplate?.header ?? '',
      body: emailTemplate?.body ?? '',
    },
  });

  const closeAndReset = () => {
    onClose();
    reset();
  };

  const {
    mutate: sendEmail,
    isPending,
    isError,
  } = useMutation({
    mutationKey: ['claimNeedsMoreInfoEmail', id],
    mutationFn: (
      notifyCustomerByEmail: CrewMerchantUi.ClaimNeedsMoreInfo['notifyCustomerByEmail'],
    ) =>
      api.store(storeId).claim(`${id}`).requestInfo({
        notifyCustomerByEmail,
        claim: {
          id,
          externalId,
          shippingAddress,
          customerEmail,
        },
      }),
    onSuccess: () => {
      closeAndReset();
      showToast('Email Sent');
    },
  });
  const formId = 'needs-more-info-email-form';

  const onSubmit: FormEventHandler = (event) => {
    handleSubmit((data) => {
      sendEmail(data);
      reset();
    })(event).catch(console.error);
  };

  return (
    <Modal
      show={show}
      title="Request More Information"
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            form={formId}
            disabled={isPending}
            loading={isPending}
          >
            Send
          </Action>
        </>
      }
    >
      <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-4">
        <dl className="grid grid-cols-5 gap-2">
          <dt>Send From:</dt>
          <dd className="col-span-4">{emailSettings?.emailSendFrom}</dd>
          <dt>Send To:</dt>
          <dd className="col-span-4">{customerEmail}</dd>
        </dl>
        <TextInput
          id="notify-customer-subject"
          label="Subject"
          required
          {...register('subject', { required: true })}
        />
        <TextInput
          id="notify-customer-header"
          label="Header"
          required
          {...register('header', { required: true })}
        />
        <TextAreaInput
          id="notify-customer-body"
          label="Body"
          required
          {...register('body', { required: true })}
        />
        {isError && (
          <SupportingText error>
            Sorry, failed to send the email. Please try again.
          </SupportingText>
        )}
      </form>
    </Modal>
  );
}
